import React, { useEffect, useState, useMemo, Suspense } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  IconButton,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  alpha,
  Zoom,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fab,
} from '@mui/material';
import {
  PlayArrow,
  School,
  TrendingUp,
  Speed,
  Psychology,
  Language,
  Analytics,
  Security,
  CheckCircle,
  Star,
  ArrowForward,
  Groups,
  AutoAwesome,
  EmojiEvents,
  ExpandMore,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';

import { Helmet } from 'react-helmet';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Scrollbar, Autoplay } from 'swiper/modules';

import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/scrollbar';
import '../../scss/Landing.scss';

interface Testimonial {
  quote: string;
  name: string;
  role: string;
  location: string;
  initials: string;
  bgColor: string;
  rating: number;
  verified: boolean;
  improvement: string;
}

interface AudienceCard {
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  cta: string;
  ctaIcon: React.ReactNode;
  link: string;
  gradient: string;
  popular?: boolean;
}

const LandingPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallMobile = useMediaQuery('(max-width:480px)');
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [showFab, setShowFab] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Enhanced scroll snap behavior for desktop only
  useEffect(() => {
    const container = document.querySelector('[data-scroll-container]') || window;

    const handleScroll = () => {
      const scrollElement = container === window ? document.documentElement : container as Element;
      const scrollTop = scrollElement.scrollTop;
      const scrollHeight = scrollElement.scrollHeight;
      const clientHeight = scrollElement.clientHeight;
      
      // Show FAB always (including in hero section)
      const shouldShow = true;
      setShowFab(shouldShow);
      
      // Determine if we're at the top (first 10% of page)
      const atTop = scrollTop < (scrollHeight * 0.1);
      setIsAtTop(atTop);
      
      // Find current section based on scroll position
      const sections = document.querySelectorAll('[data-section]');
      
      // Determine if we're at the bottom (last section - footer)
      const lastSection = sections[sections.length - 1] as HTMLElement;
      const atBottom = lastSection ? scrollTop + clientHeight >= lastSection.offsetTop + lastSection.offsetHeight - 100 : false;
      setIsAtBottom(atBottom);
      let current = 0;
      
      sections.forEach((section, index) => {
        const sectionElement = section as HTMLElement;
        const sectionTop = sectionElement.offsetTop;
        const sectionHeight = sectionElement.offsetHeight;
        
        if (scrollTop >= sectionTop - (sectionHeight / 2)) {
          current = index;
        }
      });
      
      setCurrentSection(current);
      
      // Set visibility immediately for hero section
      setIsVisible(shouldShow);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [isDesktop]);


  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.1,
    rootMargin: '50px',
    triggerOnce: false,
  });

  const { ref: videoRef, inView: videoInView } = useInView({
    threshold: 0.3,
    rootMargin: '50px',
    triggerOnce: false,
  });

  // Animation refs for different sections
  const { ref: featuresRef, inView: featuresInView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  const { ref: audienceRef, inView: audienceInView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  const { ref: testimonialsRef, inView: testimonialsInView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  const [iframeLoaded, setIframeLoaded] = useState(false);

  const { ref: qaRef, inView: qaInView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  const faqData = useMemo(
    () => [
      {
        question: 'How does Spelling Hero personalize learning for students?',
        answer:
          'Spelling Hero uses an adaptive program that creates a personalized path for each child to master spelling and build vocabulary. This ensures that students are always challenged but never overwhelmed.',
      },
      {
        question: "Can parents track their child's progress on Spelling Hero?",
        answer:
          "Yes, parents can track their child's progress through the Troubled Words Tracker and view the history of correct/incorrect words. This feature allows parents to pinpoint challenges and track every success.",
      },
      {
        question: 'Is Spelling Hero suitable for ESL learners?',
        answer:
          'Yes, Spelling Hero offers a multilingual vocabulary builder with kid-friendly definitions in 15+ languages, including 8 Indian and 8 International languages. This feature supports ESL learners and deepens comprehension for all students.',
      },
      {
        question:
          'How does Spelling Hero ensure data privacy for Indian schools?',
        answer:
          'Spelling Hero ensures unmatched privacy for Indian schools by using secure, non-PII, username-only login credentials. This compliance feature prioritizes data security and confidentiality.',
      },
      {
        question:
          'What support does Spelling Hero offer for US schools integrating with Clever/ClassLink?',
        answer:
          'Spelling Hero offers seamless integration for US schools with Clever/ClassLink, providing a powerful institutional dashboard for efficient classroom management. This feature streamlines the integration process for schools.',
      },
      {
        question:
          'What login options are available for US Schools on Spelling Hero?',
        answer:
          'US Schools can integrate with Clever/Classlink for seamless login, access an institutional-level dashboard, and benefit from secure login options designed for compliance and privacy needs.',
      },
      {
        question: 'Is there a free trial available for Spelling Hero?',
        answer:
          'Yes, Spelling Hero offers a free trial for individual users, US Schools, and Indian Schools, with the option to upgrade to Premium Membership for additional features and benefits.',
      },
    ],
    []
  );

  const QASection = useMemo(
    () => (
      <Box
        ref={qaRef}
        data-section
        sx={{
          minHeight: { xs: 'auto', lg: '100vh' },
          py: { xs: 4, sm: 6, md: 8 },
          background: 'linear-gradient(135deg, #CFF1D3 0%, #90E0A3 100%)',
          position: 'relative',
          overflow: 'hidden',
          opacity: qaInView ? 1 : 0,
          transform: qaInView
            ? 'translateY(0) scale(1)'
            : 'translateY(24px) scale(0.98)',
          transition:
            'opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 800ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: 'opacity, transform',
          ...(isDesktop && {
            scrollSnapAlign: 'start',
            scrollSnapStop: 'always',
          }),
          display: 'flex',
          alignItems: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(ellipse at 70% 30%, rgba(255,255,255,0.5) 0%, transparent 60%)',
            zIndex: 1,
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Grid
            container
            spacing={{ xs: 3, sm: 4, md: 6, lg: 8 }}
            alignItems="center"
            sx={{ height: '100%' }}
          >
            {/* Left Side - Title and Description */}
            <Grid item xs={12} lg={5}>
              <Slide direction="right" in={qaInView} timeout={800}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pr: { lg: 4 },
                    textAlign: { xs: 'center', lg: 'left' },
                  }}
                >
                  {/* Badge */}
                  <Fade in={qaInView} timeout={600}>
                    <Chip
                      label="FAQ"
                      sx={{
                        mb: { xs: 2, md: 3 },
                        background: 'rgba(255, 255, 255, 0.9)',
                        color: '#225532',
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        fontWeight: 700,
                        letterSpacing: '0.5px',
                        py: { xs: 0.5, md: 1 },
                        px: { xs: 1.5, md: 2 },
                        width: 'fit-content',
                        borderRadius: '20px',
                        border: '2px solid rgba(42, 146, 53, 0.2)',
                        boxShadow: '0 4px 16px rgba(42, 146, 53, 0.15)',
                        textTransform: 'uppercase',
                        mx: { xs: 'auto', lg: 'unset' },
                      }}
                    />
                  </Fade>

                  {/* Main Title */}
                  <Fade in={qaInView} timeout={800}>
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 800,
                        mb: { xs: 2, md: 3 },
                        fontSize: {
                          xs: '1.75rem',
                          sm: '2rem',
                          md: '2.5rem',
                          lg: '3rem',
                        },
                        lineHeight: { xs: 1.2, sm: 1.15, lg: 1.05 },
                        color: '#1f2937',
                        letterSpacing: '-0.025em',
                      }}
                    >
                      Frequently asked{' '}
                      <Box
                        component="span"
                        sx={{
                          background:
                            'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: { xs: '4px', md: '8px' },
                            left: 0,
                            right: 0,
                            height: { xs: '8px', md: '12px' },
                            background: 'rgba(42, 146, 53, 0.2)',
                            borderRadius: { xs: '4px', md: '6px' },
                            zIndex: -1,
                          },
                        }}
                      >
                        questions
                      </Box>
                    </Typography>
                  </Fade>

                  {/* Description */}
                  <Fade in={qaInView} timeout={1000}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#4a5568',
                        fontSize: { xs: '1rem', sm: '1.1rem', lg: '1.2rem' },
                        lineHeight: 1.6,
                        fontWeight: 400,
                        maxWidth: '500px',
                        mx: { xs: 'auto', lg: 'unset' },
                      }}
                    >
                      Can't find the answer you're looking for? Reach out to our
                      customer support team.
                    </Typography>
                  </Fade>
                </Box>
              </Slide>
            </Grid>

            {/* Right Side - FAQ Accordions */}
            <Grid item xs={12} lg={7}>
              <Slide direction="left" in={qaInView} timeout={1000}>
                <Box
                  sx={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: { xs: '16px', md: '24px' },
                    p: { xs: 2, sm: 3, md: 4 },
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.08)',
                    position: 'relative',
                    maxHeight: { xs: 'none', lg: '70vh' },
                    overflowY: { xs: 'visible', lg: 'auto' },
                  }}
                >
                  <Stack spacing={1}>
                    {faqData.map((faq, index) => (
                      <Fade
                        key={index}
                        in={qaInView}
                        timeout={1200 + index * 100}
                        style={{
                          transitionDelay: qaInView ? `${index * 80}ms` : '0ms',
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            borderRadius: { xs: '12px', md: '16px' },
                            overflow: 'hidden',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            '&:hover': {
                              boxShadow: '0 4px 20px rgba(42, 146, 53, 0.08)',
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          <Accordion
                            sx={{
                              background: 'transparent',
                              boxShadow: 'none',
                              '&:before': {
                                display: 'none',
                              },
                              '&.Mui-expanded': {
                                margin: 0,
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <Box
                                  sx={{
                                    width: { xs: 28, md: 32 },
                                    height: { xs: 28, md: 32 },
                                    borderRadius: { xs: '6px', md: '8px' },
                                    background:
                                      'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                  }}
                                >
                                  <ExpandMore
                                    sx={{ fontSize: { xs: 16, md: 18 } }}
                                  />
                                </Box>
                              }
                              sx={{
                                py: { xs: 2, md: 2.5 },
                                px: { xs: 2, md: 3 },
                                minHeight: 'auto',
                                '&.Mui-expanded': {
                                  minHeight: 'auto',
                                },
                                '& .MuiAccordionSummary-content': {
                                  margin: 0,
                                  '&.Mui-expanded': {
                                    margin: 0,
                                  },
                                },
                                '&:hover': {
                                  background: 'rgba(42, 146, 53, 0.02)',
                                },
                              }}
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: '#111827',
                                  fontSize: {
                                    xs: '0.9rem',
                                    sm: '1rem',
                                    md: '1.1rem',
                                  },
                                  lineHeight: 1.4,
                                  pr: 1,
                                }}
                              >
                                {faq.question}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                px: { xs: 2, md: 3 },
                                pb: { xs: 2, md: 3 },
                                pt: 0,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#374151',
                                  lineHeight: 1.7,
                                  fontSize: {
                                    xs: '0.85rem',
                                    sm: '0.95rem',
                                    md: '1rem',
                                  },
                                  fontWeight: 400,
                                }}
                              >
                                {faq.answer}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        </Paper>
                      </Fade>
                    ))}
                  </Stack>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>
    ),
    [faqData, qaRef, qaInView, isDesktop]
  );

  useEffect(() => {
    if (videoInView && !iframeLoaded) {
      setIframeLoaded(true);
    }
  }, [videoInView, iframeLoaded]);

  // Memoized data arrays for better performance
  const features = useMemo(
    () => [
      {
        icon: <AutoAwesome sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'School-Ready Word Library',
        description:
          'Master 10,000+ grade-appropriate words for long-term success. Listen in a familiar accent to make learning more effective.',
        color: theme.palette.secondary.main,
      },
      {
        icon: <Speed sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Differentiated Levels',
        description:
          'Personalized for Every Learner. Levels: Easy, Medium, Tough, Challenging. Mapped to Grades K-6 for adaptive difficulty.',
        color: theme.palette.primary.main,
      },
      {
        icon: <Analytics sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Smart Recommendations',
        description:
          'Intelligent Practice, Faster Results. AI-generated word recommendations pinpoint areas of improvement for targeted learning.',
        color: theme.palette.info.main,
      },
      {
        icon: <Language sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Multilingual Vocabulary Builder',
        description:
          'Kid-friendly definitions in 15+ languages (8 Indian, 8 International). Build Vocabulary in Their Own Language. Supports ESL learners.',
        color: theme.palette.success.main,
      },
      {
        icon: <Psychology sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Personalized Phonogram Report',
        description:
          "Learning based on phonograms, detailed reports on correct/incorrect attempts, practice specific phonograms. Understand the 'Why' Behind Spelling.",
        color: theme.palette.warning.main,
      },
      {
        icon: <TrendingUp sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Troubled Words Tracker',
        description:
          'Track and focus on challenging words for targeted learning and improvement.',
        color: theme.palette.error.main,
      },
      {
        icon: <ArrowForward sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Personalized Path',
        description:
          'Tailored learning path for each student. Adaptive program creates a personalized path for your child to master spelling and build vocabulary.',
        color: theme.palette.primary.dark,
      },
      {
        icon: <EmojiEvents sx={{ fontSize: { xs: 24, md: 28 } }} />,
        title: 'Gamified Learning',
        description:
          'Unlock new levels and earn fun badges, transforming practice into an exciting and motivating journey. Turn Practice into Play.',
        color: '#9C27B0',
      },
    ],
    [theme]
  );

  const audienceCards: AudienceCard[] = useMemo(
    () => [
      {
        title: 'For Parents & Individuals',
        subtitle: 'Personalized Learning at Home',
        description:
          'Perfect for students, professionals, and lifelong learners seeking to enhance their spelling and vocabulary skills.',
        features: [
          'AI-powered personalized learning paths',
          'Comprehensive progress analytics',
          'Extended 30-day premium trial',
          'Unlimited practice sessions',
          'Self-paced adaptive curriculum',
          'Mobile and desktop sync',
        ],
        cta: 'Start Your Free Trial',
        ctaIcon: <PlayArrow />,
        link: '/signup-individual',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        popular: true,
      },
      {
        title: 'For Districts & Schools with SSO',
        subtitle: 'Enterprise-grade management through school systems',
        description:
          'Seamlessly integrate with Clever or ClassLink to provide immediate, hassle-free access for all your students.',
        features: [
          'Clever & ClassLink SSO integration',
          'Advanced teacher dashboard',
          'Bulk student management',
          'Real-time performance analytics',
          'Curriculum alignment tools',
          'COPPA/FERPA compliant',
        ],
        cta: 'Get Started with SSO',
        ctaIcon: <School />,
        link: '/signup-school-sso',
        gradient: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
      },
      {
        title: 'For Anonymous Username Accounts',
        subtitle: 'Maximum student privacy and direct control',
        description:
          'Get started in minutes by creating accounts with just a username—no student emails, phone numbers, or PII required.',
        features: [
          'Username-only authentication',
          'Zero personal data collection',
          'Indian compliance standards',
          'Secure local data storage',
          'Offline capability support',
          'Regional language support',
        ],
        cta: 'Create Your School Account',
        ctaIcon: <Security />,
        link: '/signup-school-reg',
        gradient: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
      },
    ],
    []
  );

  // Sample testimonials data (memoized) to be used by TestimonialsSection
  const testimonials: Testimonial[] = useMemo(
    () => [
      {
        quote:
          'The multiple accent feature has completely transformed how I approach pronunciation. I can now confidently attempt any word!',
        name: 'Amogh S.',
        role: 'Grade 8 Student',
        location: 'Mumbai, India',
        initials: 'AS',
        bgColor: '#667eea',
        rating: 5,
        verified: true,
        improvement: '89% accuracy boost',
      },
      {
        quote:
          'The variable speed playback helps me break down complex words and understand each sound pattern clearly.',
        name: 'Liam B.',
        role: 'Elementary Student',
        location: 'Texas, USA',
        initials: 'LB',
        bgColor: '#4CAF50',
        rating: 5,
        verified: true,
        improvement: '3x faster learning',
      },
      {
        quote:
          'Every practice session builds my confidence. The progress tracking motivates me to keep improving daily.',
        name: 'Kumar S.',
        role: 'High School Student',
        location: 'Delhi, India',
        initials: 'KS',
        bgColor: '#FF9800',
        rating: 5,
        verified: true,
        improvement: '95% retention rate',
      },
      {
        quote:
          'The AI-powered difficulty adjustment keeps me challenged without being overwhelmed. Perfect balance for learning!',
        name: 'Emma C.',
        role: 'Middle School Student',
        location: 'California, USA',
        initials: 'EC',
        bgColor: '#9C27B0',
        rating: 5,
        verified: true,
        improvement: '2x vocabulary growth',
      },
      {
        quote:
          'Regional language support helped me understand pronunciation nuances. This platform understands diverse learning needs.',
        name: 'Priya M.',
        role: 'High School Student',
        location: 'Bangalore, India',
        initials: 'PM',
        bgColor: '#F44336',
        rating: 5,
        verified: true,
        improvement: '85% pronunciation improvement',
      },
      {
        quote:
          'The gamified learning approach makes spelling practice fun and engaging. My kids actually look forward to their sessions!',
        name: 'Sarah J.',
        role: 'Parent & Teacher',
        location: 'London, UK',
        initials: 'SJ',
        bgColor: '#2196F3',
        rating: 5,
        verified: true,
        improvement: 'Learning engagement up 300%',
      },
    ],
    []
  );

  // RESTORE: Testimonials carousel (auto-scrolling left-to-right)
  const TestimonialsSection = useMemo(
    () => (
      <Box
        ref={testimonialsRef}
        data-section
        sx={{
          minHeight: { xs: 'auto', lg: '100vh' },
          py: { xs: 4, sm: 6, md: 8 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          ...(isDesktop && { scrollSnapAlign: 'start' }),
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          opacity: testimonialsInView ? 1 : 0,
          transform: testimonialsInView ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background:
              'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)',
          },
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Fade in={testimonialsInView} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: {
                    xs: '1.75rem',
                    sm: '2rem',
                    md: '2.5rem',
                    lg: '3rem',
                  },
                  fontWeight: 800,
                  mt: { xs: 2, md: 6 },
                  mb: { xs: 2, md: 3 },
                  color: '#1A1A1A',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                Student{' '}
                <Box
                  component="span"
                  sx={{
                    background:
                      'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    position: 'relative',
                    mb: 3,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: { xs: '2px', md: '4px' },
                      left: 0,
                      right: 0,
                      height: { xs: '8px', md: '12px' },
                      background: 'rgba(42, 146, 53, 0.15)',
                      borderRadius: { xs: '4px', md: '6px' },
                      zIndex: -1,
                    },
                  }}
                >
                  Success Stories
                </Box>
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  color: '#64748b',
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                See the difference our platform makes in real learning outcomes
              </Typography>
            </Box>
          </Fade>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <Swiper
                modules={[Pagination, Scrollbar, Autoplay]}
                spaceBetween={isSmallMobile ? 16 : isMobile ? 24 : 32}
                loop={true}
                slidesPerView={1}
                pagination={{
                  clickable: true,
                  bulletActiveClass: 'swiper-pagination-bullet-active',
                }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                breakpoints={{
                  480: { slidesPerView: 1, spaceBetween: 20 },
                  768: { slidesPerView: 2, spaceBetween: 24 },
                  1024: { slidesPerView: 2, spaceBetween: 28 },
                  1200: { slidesPerView: 3, spaceBetween: 32 },
                }}
                style={{
                  paddingBottom: isSmallMobile ? '28px' : '48px',
                  height: 'auto',
                  paddingLeft: isSmallMobile ? '8px' : '0',
                  paddingRight: isSmallMobile ? '8px' : '0',
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <SwiperSlide key={index}>
                    <Card
                      sx={{
                        height: '100%',
                        minHeight: { xs: 240, sm: 280, md: 320 },
                        borderRadius: { xs: '16px', md: '24px' },
                        background: '#ffffff',
                        border: '1px solid #f1f5f9',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        mx: isSmallMobile ? 1 : 0,
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
                          border: `1px solid ${alpha(
                            testimonial.bgColor,
                            0.3
                          )}`,
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(135deg, ${
                            testimonial.bgColor
                          } 0%, ${alpha(testimonial.bgColor, 0.7)} 100%)`,
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          p: { xs: 2, sm: 2.5, md: 3 },
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ mb: { xs: 2, md: 3 } }}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: { xs: 1.5, md: 2 },
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <Star
                                  key={i}
                                  sx={{
                                    color: '#FFD700',
                                    fontSize: { xs: 14, md: 16 },
                                  }}
                                />
                              ))}
                            </Box>
                            {testimonial.verified && (
                              <Chip
                                icon={
                                  <CheckCircle
                                    sx={{ fontSize: { xs: 10, md: 12 } }}
                                  />
                                }
                                label="Verified"
                                size="small"
                                sx={{
                                  background:
                                    'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                                  color: 'white',
                                  fontSize: { xs: '0.7rem', md: '0.75rem' },
                                  height: { xs: '20px', md: '24px' },
                                }}
                              />
                            )}
                          </Box>

                          <Chip
                            label={testimonial.improvement}
                            sx={{
                              background: `linear-gradient(135deg, ${alpha(
                                testimonial.bgColor,
                                0.15
                              )} 0%, ${alpha(testimonial.bgColor, 0.08)} 100%)`,
                              color: testimonial.bgColor,
                              fontWeight: 700,
                              fontSize: { xs: '0.7rem', md: '0.75rem' },
                              border: `1px solid ${alpha(
                                testimonial.bgColor,
                                0.2
                              )}`,
                              height: { xs: '24px', md: '28px' },
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 2, md: 3 },
                            position: 'relative',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.7,
                              fontSize: {
                                xs: '0.85rem',
                                sm: '0.9rem',
                                md: '0.95rem',
                              },
                              color: '#2d3748',
                              fontStyle: 'italic',
                              pl: { xs: 1.5, md: 2 },
                            }}
                          >
                            {testimonial.quote}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: 'auto',
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 40, md: 48 },
                              height: { xs: 40, md: 48 },
                              borderRadius: { xs: '12px', md: '16px' },
                              background: `linear-gradient(135deg, ${
                                testimonial.bgColor
                              } 0%, ${alpha(testimonial.bgColor, 0.8)} 100%)`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: { xs: '0.9rem', md: '1.1rem' },
                              fontWeight: 800,
                              mr: { xs: 2, md: 3 },
                              boxShadow: `0 8px 25px ${alpha(
                                testimonial.bgColor,
                                0.3
                              )}`,
                              border: `2px solid ${alpha(
                                testimonial.bgColor,
                                0.2
                              )}`,
                            }}
                          >
                            {testimonial.initials}
                          </Box>

                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 800,
                                color: '#1a202c',
                                fontSize: { xs: '0.85rem', md: '0.95rem' },
                                mb: 0.5,
                              }}
                            >
                              {testimonial.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: testimonial.bgColor,
                                fontWeight: 700,
                                fontSize: { xs: '0.75rem', md: '0.8rem' },
                                mb: 0.5,
                                display: 'block',
                              }}
                            >
                              {testimonial.role}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#718096',
                                fontSize: { xs: '0.7rem', md: '0.75rem' },
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 500,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  width: { xs: 4, md: 5 },
                                  height: { xs: 4, md: 5 },
                                  borderRadius: '50%',
                                  background: '#4CAF50',
                                  mr: 1,
                                  display: 'inline-block',
                                }}
                              />
                              {testimonial.location}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Box>
          </Box>
        </Container>
      </Box>
    ),
    [testimonials, testimonialsRef, testimonialsInView, isDesktop]
  );

  // Optimized hero section component with animation reset
  const HeroSection = useMemo(
    () => (
      <Box
        ref={heroRef}
        data-section
        sx={{
          minHeight: { xs: 'auto', lg: '100vh' },
          background: 'linear-gradient(135deg, #CFF1D3 0%, #90E0A3 100%)',
          position: 'relative',
          display: 'flex',
          // center content on mobile for a balanced look, keep center on desktop
          alignItems: { xs: 'flex-start', sm: 'center', lg: 'center' },
          overflow: 'hidden',
          ...(isDesktop && { scrollSnapAlign: 'start' }),
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            px: { xs: 2, sm: 3, md: 6 },
            // offset by navbar height on very small screens so content doesn't tuck under header
            pt: { xs: '64px', sm: 3, md: 6 },
          }}
        >
          <Grid
            container
            spacing={{ xs: 3, lg: 6 }}
            alignItems="center"
            sx={{ height: '100%', py: { xs: 2, md: 6 } }}
          >
            <Grid item xs={12} lg={6}>
              <Box
                sx={{
                  textAlign: { xs: 'center', lg: 'left' },
                  px: { xs: 0, sm: 2 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'center', lg: 'flex-start' },
                }}
              >
                <Slide direction="down" in={heroInView} timeout={800}>
                  <Chip
                    icon={<AutoAwesome sx={{ fontSize: { xs: 18, md: 20 } }} />}
                    label="New AI Features Available"
                    sx={{
                      mb: { xs: 1.5, md: 4 },
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#225532',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      py: 1.5,
                      px: 3,
                      borderRadius: '30px',
                      backdropFilter: 'blur(20px)',
                      border: '2px solid rgba(34, 85, 50, 0.2)',
                      boxShadow: '0 8px 32px rgba(34, 85, 50, 0.15)',
                    }}
                  />
                </Slide>

                <Slide direction="right" in={heroInView} timeout={1000}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: {
                        xs: '1.8rem',
                        sm: '2.5rem',
                        md: '3rem',
                        lg: '3.5rem',
                      },
                      fontWeight: 900,
                      lineHeight: { xs: 1.02, md: 1.06 },
                      mb: { xs: 1.5, md: 3 },
                      color: '#225532',
                      letterSpacing: '-0.03em',
                      '& .highlight': {
                        background:
                          'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      },
                      '& .normal': {
                        color: '#1f2937',
                      },
                    }}
                  >
                    The Path to
                    <br />
                    <span className="highlight">Spelling Mastery</span>
                    <br />
                    <span className="normal">and Lifelong Confidence</span>
                  </Typography>
                </Slide>

                <Slide direction="up" in={heroInView} timeout={1200}>
                  <Typography
                    variant="h5"
                    sx={{
                      mb: { xs: 2.5, md: 5 },
                      color: '#225532',
                      fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.35rem' },
                      lineHeight: 1.55,
                      fontWeight: 400,
                      maxWidth: { xs: '95%', sm: '560px', md: '650px' },
                      opacity: 0.95,
                      mx: { xs: 'auto', lg: 'unset' },
                    }}
                  >
                    Our adaptive program creates a personalized path for your
                    child to master spelling and build vocabulary. Students
                    thrive, teachers reclaim hours from lesson planning, and
                    parents witness lasting academic success.
                  </Typography>
                </Slide>

                <Zoom in timeout={1400}>
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                      md: 'row',
                      lg: 'row',
                    }}
                    spacing={2}
                    sx={{
                      mb: { xs: 3.5, md: 6 },
                      width: '100%',
                      // center CTA on mobile & tablet, align left on large screens
                      justifyContent: {
                        xs: 'center',
                        md: 'center',
                        lg: 'flex-start',
                      },
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={
                        <ArrowForward
                          sx={{ ml: 1, fontSize: { xs: '1rem', md: '1.1rem' } }}
                        />
                      }
                      component={Link}
                      to="/signup"
                      sx={{
                        fontSize: { xs: '0.95rem', sm: '1rem', md: '1rem' },
                        py: { xs: 1.25, sm: 1.75, md: 2 },
                        px: { xs: 3, sm: 4, md: 4 },
                        borderRadius: '50px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: { xs: 'auto', sm: '200px', md: 160 },
                        width: { xs: '100%', md: 'auto', lg: 'auto' },
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)',
                        color: '#1F2937',
                        border: 'none',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          background:
                            'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      Try it for free
                    </Button>
                  </Stack>
                </Zoom>

                <Fade in timeout={1600}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 2,
                      color: '#225532',
                      background: 'rgba(255,255,255,0.8)',
                      border: '2px solid rgba(34, 85, 50, 0.15)',
                      borderRadius: '20px',
                      px: 3,
                      py: 2,
                      width: 'fit-content',
                      boxShadow: '0 8px 32px rgba(34,85,50,0.12)',
                      mx: { xs: 'auto', lg: 'unset' },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <School
                        sx={{
                          color: '#2A9235',
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                          fontWeight: 600,
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        500+ Teachers
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Groups
                        sx={{
                          color: '#2A9235',
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                          fontWeight: 600,
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        6,000+ Students
                      </Typography>
                    </Box>

                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <EmojiEvents
                        sx={{
                          color: '#2A9235',
                          fontSize: { xs: '1.1rem', md: '1.3rem' },
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: { xs: '0.9rem', md: '1.1rem' },
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        Grades K-6
                      </Typography>
                    </Box>
                  </Box>
                </Fade>
              </Box>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Slide direction="left" in={heroInView} timeout={1200}>
                <Box
                  ref={videoRef}
                  sx={{
                    position: 'relative',
                    paddingTop: '56.25%',
                    borderRadius: { xs: '10px', md: '24px' },
                    overflow: 'hidden',
                    boxShadow: {
                      xs: '0 6px 18px rgba(0,0,0,0.08)',
                      md: '0 30px 80px rgba(0,0,0,0.25)',
                    },
                    border: '3px solid rgba(255,255,255,0.3)',
                    mt: { xs: 3, md: 0 },
                    // constrain width on small screens so it doesn't feel too large
                    width: { xs: '100%', sm: '92%', md: '100%' },
                    maxWidth: { xs: 540, md: '100%' },
                    mx: { xs: 'auto', lg: 'unset' },
                  }}
                >
                  {iframeLoaded ? (
                    <iframe
                      src="https://www.youtube.com/embed/IIID-HfcGN4?autoplay=0&rel=0&showinfo=0"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: '20px',
                      }}
                      allowFullScreen
                      title="Spelling Hero Platform Demo"
                      loading="lazy"
                    />
                  ) : (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: alpha('#fff', 0.1),
                      }}
                    >
                      <IconButton
                        sx={{
                          width: 80,
                          height: 80,
                          background:
                            'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                          color: 'white',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                        onClick={() => setIframeLoaded(true)}
                      >
                        <PlayArrow sx={{ fontSize: 40 }} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>
    ),
    [heroRef, heroInView, videoRef, iframeLoaded, setIframeLoaded, isDesktop]
  );

  const KeyFeaturesSection = useMemo(
    () => (
      <Box
        ref={featuresRef}
        data-section
        sx={{
          minHeight: { xs: 'auto', lg: '100vh' },
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          ...(isDesktop && { scrollSnapAlign: 'start' }),
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Fade in={featuresInView} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                  fontWeight: 700,
                  mb: 3,
                  color: '#2A9235',
                  letterSpacing: '-0.02em',
                  lineHeight: 1.1,
                }}
              >
                The Complete Toolkit for{' '}
                <Box
                  component="span"
                  sx={{
                    background:
                      'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '4px',
                      left: 0,
                      right: 0,
                      height: '12px',
                      background: 'rgba(42, 146, 53, 0.15)',
                      borderRadius: '6px',
                      zIndex: -1,
                    },
                  }}
                >
                  Spelling Mastery
                </Box>
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  color: '#6b7280',
                  maxWidth: '700px',
                  mx: 'auto',
                  lineHeight: 1.6,
                }}
              >
                Unlock premium features and take your spelling journey to the
                next level
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Zoom
                  in={featuresInView}
                  timeout={1000 + index * 150}
                  style={{
                    transitionDelay: featuresInView
                      ? `${index * 100}ms`
                      : '0ms',
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      border: '1px solid #86efac',
                      background:
                        'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 2px 8px rgba(42, 146, 53, 0.08)',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow:
                          '0 20px 40px rgba(42, 146, 53, 0.15), 0 8px 16px rgba(42, 146, 53, 0.1)',
                        background:
                          'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                        border: '1px solid #4ade80',
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                          background: '#2A9235',
                          boxShadow: '0 8px 20px rgba(42, 146, 53, 0.3)',
                          '& svg': {
                            color: '#ffffff',
                            transform: 'scale(1.1)',
                          },
                        },
                        '& .feature-title': {
                          color: '#225532',
                          transform: 'translateY(-2px)',
                        },
                        '& .feature-description': {
                          color: '#374151',
                          transform: 'translateY(-2px)',
                        },
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        className="feature-icon"
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '12px',
                          background: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          border: '1px solid #dcfce7',
                          boxShadow: '0 2px 4px rgba(42, 146, 53, 0.1)',
                          '& svg': {
                            color: '#2A9235',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          },
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        className="feature-title"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: '1rem',
                          color: '#2A9235',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          lineHeight: 1.3,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="feature-description"
                        sx={{
                          color: '#374151',
                          lineHeight: 1.5,
                          fontSize: '0.9rem',
                          flexGrow: 1,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    ),
    [features, featuresRef, featuresInView, isDesktop]
  );

  const AudienceCardsSection = useMemo(
    () => (
      <Box
        ref={audienceRef}
        data-section
        sx={{
          minHeight: { xs: 'auto', lg: '100vh' },
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #CFF1D3 0%, #90E0A3 100%)',
          ...(isDesktop && { scrollSnapAlign: 'start' }),
          display: 'flex',
          alignItems: { xs: 'center', lg: 'flex-start' },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: { xs: '100%', lg: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: { xs: 'flex-start', lg: 'flex-start' },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Fade in={audienceInView} timeout={800}>
            <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  fontWeight: 700,
                  mt: 6,
                  mb: 2,
                  color: '#1A1A1A',
                }}
              >
                Find Your Perfect Fit
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 700,
                  mb: 4,
                  background:
                    'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Plans for Every Learning Goal
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 400,
                  mb: 2,
                  color: 'text.secondary',
                  maxWidth: '700px',
                  mx: 'auto',
                }}
              >
                Whether you're a parent guiding a child, a teacher managing a
                classroom, or a student pursuing personal growth, we have the
                perfect plan for your learning journey.
              </Typography>
            </Box>
          </Fade>

          <Grid container spacing={4} sx={{ justifyContent: 'center' }}>
            {audienceCards.map((card, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <Zoom
                  in={audienceInView}
                  timeout={1000 + index * 200}
                  style={{
                    transitionDelay: audienceInView
                      ? `${index * 100}ms`
                      : '0ms',
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      borderRadius: '16px',
                      p: 3,
                      boxShadow: '0 20px 50px rgba(34,85,50,0.06)',
                      border: '1px solid rgba(255,255,255,0.6)',
                      background: '#ffffff',
                      position: 'relative',
                      alignItems: 'stretch',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 25px 60px rgba(34,85,50,0.12)',
                      },
                    }}
                  >
                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: '12px',
                          background:
                            'linear-gradient(135deg, #2A9235 0%, #225532 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        {card.ctaIcon}
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.5rem',
                          color: '#225532',
                          mb: 1,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#2A9235',
                          fontSize: '1rem',
                          fontWeight: 700,
                          mb: 2,
                        }}
                      >
                        {card.subtitle}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#4a5568',
                          lineHeight: 1.6,
                          fontSize: '0.9rem',
                        }}
                      >
                        {card.description}
                      </Typography>
                    </Box>

                    {/* Features List */}
                    <Box sx={{ mb: 1, flexGrow: 0.5 }}>
                      <Stack spacing={2}>
                        {card.features.map((f, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <CheckCircle
                              sx={{
                                mr: 1,
                                fontSize: 16,
                                color: '#2A9235',
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '0.9rem',
                                color: '#225532',
                                fontWeight: 500,
                              }}
                            >
                              {f}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>

                    {/* CTA Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      component={Link}
                      to={card.link}
                      sx={{
                        py: 1.5,
                        backgroundColor: '#2A9235',
                        color: 'white',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        borderRadius: '8px',
                        '&:hover': {
                          backgroundColor: '#225532',
                        },
                      }}
                    >
                      {card.cta}
                    </Button>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    ),
    [audienceCards, audienceRef, audienceInView, isDesktop]
  );

  return (
    <>
      <Helmet>
        <title>
          Spelling Hero - AI-Powered Spelling & Vocabulary Mastery Platform
        </title>
        <meta
          name="description"
          content="Transform your spelling skills with AI-powered learning. 10,000+ words, multilingual support, and adaptive learning for students and educators worldwide."
        />
        <link rel="canonical" href="https://spellinghero.app" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <Suspense fallback={<div>Loading...</div>}>
        <header>
          <Navbar />
        </header>
      </Suspense>

      <Box
        component="main"
        data-scroll-container
        sx={{
          height: { xs: 'auto', lg: '100vh' },
          overflowY: { xs: 'visible', lg: 'auto' },
          scrollSnapType: isDesktop ? 'y mandatory' : 'none',
          scrollBehavior: isDesktop ? 'smooth' : 'auto',
          px: { xs: 0, sm: 0 },
        }}
      >
        {HeroSection}

        {KeyFeaturesSection}

        {AudienceCardsSection}

        {TestimonialsSection}

        {QASection}

      <Suspense fallback={<div>Loading...</div>}>
          <Box
            data-section
            sx={{ scrollSnapAlign: 'start', scrollSnapStop: 'always' }}
            >
            <Footer hideFab={true}/>
          </Box>
        </Suspense>
            </Box>

            {/* Downward FAB - Visible except at bottom */}
            {showFab && !isAtBottom && (
        <Fab
          onClick={() => {
            const sections = document.querySelectorAll('[data-section]');
            const container = document.querySelector('[data-scroll-container]') || window;
            const targetSection = Math.min(currentSection + 1, sections.length - 1);
            const targetElement = sections[targetSection] as HTMLElement;
            
            if (targetElement) {
              if (container === window) {
                window.scrollTo({
                  top: targetElement.offsetTop,
                  behavior: 'smooth'
                });
              } else {
                (container as Element).scrollTo({
                  top: targetElement.offsetTop,
                  behavior: 'smooth'
                });
              }
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
            color: 'white',
            boxShadow: '0 16px 48px rgba(34, 197, 94, 0.4)',
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(100px) scale(0.8)',
            opacity: isVisible ? 1 : 0,
            transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: '1.6s',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            zIndex: 1300,
            animation: isVisible ? 'fabPulse 2s ease-in-out infinite, fabFloat 3s ease-in-out infinite' : 'none',
            '&:hover': {
              transform: 'translateY(-12px) scale(1.15) rotate(-5deg)',
              boxShadow: '0 24px 64px rgba(34, 197, 94, 0.6)',
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #166534 100%)',
              animation: 'fabBounce 0.6s ease-in-out, fabPulse 1.5s ease-in-out infinite',
            },
            '&:active': {
              transform: 'translateY(-8px) scale(1.05)',
              animation: 'fabClick 0.3s ease-in-out',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -3,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              backgroundSize: '400% 400%',
              animation: 'shimmer 4s ease-in-out infinite',
              opacity: 0.7,
              zIndex: -1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -8,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              opacity: 0.3,
              zIndex: -2,
              animation: 'fabRipple 2s ease-in-out infinite',
            }
          }}
        >
          <KeyboardArrowDown
            sx={{
              fontSize: 36,
              animation: 'float 2s ease-in-out infinite',
              transition: 'all 0.3s ease',
            }}
          />
        </Fab>
      )}

      {/* Upward FAB - Only visible when scrolled down */}
      {showFab && !isAtTop && (
        <Fab
          onClick={() => {
            const sections = document.querySelectorAll('[data-section]');
            const container = document.querySelector('[data-scroll-container]') || window;
            // If we're at the bottom (footer), go to FAQ section (index 4), otherwise go to previous section
            const targetSection = isAtBottom ? 4 : Math.max(currentSection - 1, 0);
            const targetElement = sections[targetSection] as HTMLElement;
            
            if (targetElement) {
              if (container === window) {
                window.scrollTo({
                  top: targetElement.offsetTop,
                  behavior: 'smooth'
                });
              } else {
                (container as Element).scrollTo({
                  top: targetElement.offsetTop,
                  behavior: 'smooth'
                });
              }
            }
          }}
          sx={{
            position: 'fixed',
            bottom: 112, // Position above the down FAB
            right: 32,
            width: 64,
            height: 64,
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
            color: 'white',
            boxShadow: '0 16px 48px rgba(34, 197, 94, 0.4)',
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(100px) scale(0.8)',
            opacity: isVisible ? 1 : 0,
            transition: 'all 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transitionDelay: '1.6s',
            border: '3px solid rgba(255, 255, 255, 0.3)',
            backdropFilter: 'blur(10px)',
            zIndex: 1300,
            animation: isVisible ? 'fabPulse 2.2s ease-in-out infinite, fabFloat 3.2s ease-in-out infinite' : 'none',
            '&:hover': {
              transform: 'translateY(-12px) scale(1.15) rotate(5deg)',
              boxShadow: '0 24px 64px rgba(34, 197, 94, 0.6)',
              background: 'linear-gradient(135deg, #16a34a 0%, #15803d 50%, #166534 100%)',
              animation: 'fabBounce 0.6s ease-in-out, fabPulse 1.5s ease-in-out infinite',
            },
            '&:active': {
              transform: 'translateY(-8px) scale(1.05)',
              animation: 'fabClick 0.3s ease-in-out',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: -3,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              backgroundSize: '400% 400%',
              animation: 'shimmer 4.2s ease-in-out infinite',
              opacity: 0.7,
              zIndex: -1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: -8,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e)',
              opacity: 0.3,
              zIndex: -2,
              animation: 'fabRipple 2.2s ease-in-out infinite',
            }
          }}
        >
          <KeyboardArrowUp
            sx={{
              fontSize: 36,
              animation: 'float 2s ease-in-out infinite',
              transition: 'all 0.3s ease',
            }}
          />
        </Fab>
      )}

      {/* Add CSS animations */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes float {
          0% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
          100% { transform: translateY(0); }
        }

        @keyframes fabPulse {
          0% { 
            box-shadow: 0 16px 48px rgba(34, 197, 94, 0.4);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 20px 60px rgba(34, 197, 94, 0.6);
            transform: scale(1.02);
          }
          100% { 
            box-shadow: 0 16px 48px rgba(34, 197, 94, 0.4);
            transform: scale(1);
          }
        }

        @keyframes fabFloat {
          0% { transform: translateY(0px) rotate(0deg); }
          25% { transform: translateY(-3px) rotate(1deg); }
          50% { transform: translateY(-6px) rotate(0deg); }
          75% { transform: translateY(-3px) rotate(-1deg); }
          100% { transform: translateY(0px) rotate(0deg); }
        }

        @keyframes fabBounce {
          0% { transform: translateY(0) scale(1); }
          25% { transform: translateY(-8px) scale(1.1); }
          50% { transform: translateY(-12px) scale(1.15); }
          75% { transform: translateY(-6px) scale(1.05); }
          100% { transform: translateY(0) scale(1); }
        }

        @keyframes fabClick {
          0% { transform: scale(1); }
          50% { transform: scale(0.95); }
          100% { transform: scale(1); }
        }

        @keyframes fabRipple {
          0% { 
            transform: scale(1);
            opacity: 0.3;
          }
          50% { 
            transform: scale(1.2);
            opacity: 0.1;
          }
          100% { 
            transform: scale(1.4);
            opacity: 0;
          }
        }

        @keyframes fabGlow {
          0% { 
            filter: brightness(1) drop-shadow(0 0 5px rgba(34, 197, 94, 0.3));
          }
          50% { 
            filter: brightness(1.1) drop-shadow(0 0 15px rgba(34, 197, 94, 0.6));
          }
          100% { 
            filter: brightness(1) drop-shadow(0 0 5px rgba(34, 197, 94, 0.3));
          }
        }
      `}</style>
    </>
  );
};

export default React.memo(LandingPage);
